import { captureException } from '@sentry/browser';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { api } from 'controllers/network/apiClient';
import useAuth from 'hooks/useAuth';
import { MockedEntitlements } from 'providers/entitlements/fixtures';
import { EntitlementKey, EntitlementKeys, EntitlementsResponse } from './types';

export const ENTITLEMENTS_API_URL = '/v2/shops/entitlements/';
const ENTITLEMENTS_QUERY_KEY = 'entitlements';
export const FALLBACK_REFETCH_INTERVAL_MS = 30_000;

interface EntitlementsQueryData extends EntitlementsResponse {
  /** True when the request failed and MockedEntitlements are being served instead */
  isFallback?: boolean;
}

export const checkEntitlementKeyMismatch = (
  apiKeys: string[],
  hardcodedKeys: EntitlementKey[],
) => {
  const missingInHardcoded = apiKeys.filter(
    (key) => !hardcodedKeys.includes(key as EntitlementKey),
  );
  const missingInAPI = hardcodedKeys.filter((key) => !apiKeys.includes(key));

  if (process.env.NODE_ENV === 'development') {
    if (missingInHardcoded.length > 0) {
      console.warn(
        '[Entitlements] Keys found in API but missing from hardcoded list:',
        missingInHardcoded,
      );
    }
    if (missingInAPI.length > 0) {
      console.warn(
        '[Entitlements] Keys in hardcoded list but missing from API:',
        missingInAPI,
      );
    }
  }

  return {
    missingInHardcoded,
    missingInAPI,
  };
};

const getEntitlements = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof EntitlementsQueryKeys['getEntitlements']>
>): Promise<EntitlementsQueryData> => {
  const [, { shopId }] = queryKey;

  try {
    const response = await api.get<EntitlementsResponse>(ENTITLEMENTS_API_URL, {
      timeout: 5000,
    });

    // Check for key mismatches in development
    checkEntitlementKeyMismatch(
      Object.keys(response.entitlements),
      Object.values(EntitlementKeys),
    );

    return response;
  } catch (error) {
    captureException(error, { extra: { shopId } });

    // Return mocked data as a fallback
    return {
      entitlements: MockedEntitlements,
      merchant_id: shopId,
      isFallback: true,
    };
  }
};

const EntitlementsQueryKeys = {
  getEntitlements: (shopId: number) =>
    [ENTITLEMENTS_QUERY_KEY, { shopId }] as const,
};

/** Do not call this hook directly! Only interact with Entitlements via useEntitlements() */
export const useGetEntitlements = () => {
  const { isLoggedIn, getShopId } = useAuth();

  return useQuery({
    queryKey: EntitlementsQueryKeys.getEntitlements(getShopId()),
    queryFn: getEntitlements,
    // Keep retrying in the background so a transient failure doesn't pin the
    // shop to fallback entitlements until the next page load.
    refetchInterval: (data) =>
      data?.isFallback ? FALLBACK_REFETCH_INTERVAL_MS : false,
    enabled: isLoggedIn,
  });
};
